import { useQuery } from '@tanstack/react-query';
import type { inferInput, inferOutput } from '@trpc/tanstack-react-query';
import { useMemo, useState } from 'react';
import { nameLookupCaps } from '../components/filters/nameCaps';
import { useTRPC } from './trpc';

type TRPC = ReturnType<typeof useTRPC>;
type PanelInput = inferInput<TRPC['filters']['panel']>;
type NamesInput = inferInput<TRPC['filters']['names']>;

export type PanelDimensions = PanelInput['dimensions'];
export type SelectedFilters = PanelInput['selected'];
export type PanelValues = inferOutput<TRPC['filters']['panel']>;
export type NameDimension = NamesInput['names'][number];
export type DateWindow = NamesInput['dateWindow'];

export function hasAccounts(accountIds?: number[]): boolean {
  return (accountIds?.length ?? 0) > 0;
}

export function useFilterCatalog(accountIds?: number[]) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.filters.catalog.queryOptions({ accountIds }),
    enabled: hasAccounts(accountIds),
  });
}

export function useMetrics(accountIds?: number[]) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.filters.metrics.queryOptions({ accountIds }),
    enabled: hasAccounts(accountIds),
  });
}

export function useClients() {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.auth.currentClient.queryOptions(),
    select: (client) => (client ? [client] : []),
  });
}

/** `dimensions` undefined asks for every filter the scope's panel serves. */
export function usePanelValues(
  dimensions: PanelDimensions,
  selected?: SelectedFilters,
  dateWindow?: DateWindow,
  accountIds?: number[],
) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.filters.panel.queryOptions({ dimensions, selected, dateWindow, accountIds }),
    enabled: (dimensions === undefined || dimensions.length > 0) && hasAccounts(accountIds),
    placeholderData: (previous) => previous,
  });
}

export function useNameValues(
  dimension: NameDimension,
  dateWindow: DateWindow,
  accountIds?: number[],
  selected?: SelectedFilters,
) {
  const trpc = useTRPC();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const query = useQuery({
    ...trpc.filters.names.queryOptions({
      names: [dimension],
      dateWindow,
      ...(debouncedSearch ? { nameSearch: { [dimension]: debouncedSearch } } : {}),
      caps: nameLookupCaps(dimension),
      ...(selected ? { selected } : {}),
      accountIds,
    }),
    enabled: hasAccounts(accountIds),
  });

  const onSearchChange = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (nextSearch: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => setDebouncedSearch(nextSearch), 300);
    };
  }, []);

  return { ...query, onSearchChange };
}
